import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { PagedResult } from '../../../shared/models/paged-result/paged-result';
import { PortfolioHistoryList } from '../../models/portfolio/portfolio-history-list';
import { HistoryFilter } from '../../../pages/my-portfolio/history/history-filter';
import moment from 'moment';
import { PortfolioListModel } from '../../models/portfolio/portfolio-list.model';

@Injectable()
export class PortfolioService {
  private route = environment.apiServer + 'Api/v1/Users/{userId}/{role}/portfolio';

  constructor(private http: HttpClient) {}

  getPortfolio(currentPage: number, take: number, isDigitalCert?: boolean, includeMonthlyStatements = false): Observable<any> {
    type GetPortfolioParams = {
      currentPage: string;
      take: string;
      isDigitalCert?: boolean;
      includeMonthlyStatements: boolean;
    };

    let params: GetPortfolioParams = {
      currentPage: currentPage.toString(),
      take: take.toString(),
      includeMonthlyStatements: includeMonthlyStatements,
    };

    if (isDigitalCert) {
      params.isDigitalCert = true;
    }

    return this.http.get(this.route + '/details', { params: params }).pipe(
      map((data: any) => {
        let mergedPortfolio:Array<PortfolioListModel> = [];
        let mergePortfolios = (arr:Array<PortfolioListModel>) => {
          const byRegistrationNumber = new Map<string, PortfolioListModel>();
          for (const element of arr) {
            // Key by company + counter so a company's distinct counters are kept
            // as separate rows (grouped in the UI); only true duplicates of the
            // same counter are merged.
            // A missing registrationNumber must not become a shared "undefined"/"null" key
            // that merges unrelated companies together. Key on tickerCode when present (it's
            // unique per counter); otherwise fall back to a key unique to this row so it never
            // collides with another row lacking a registration number.
            const registrationNumber = element.company?.registrationNumber;
            const key = registrationNumber
              ? 'reg::' + String(registrationNumber) + '::' + (element.tickerCode ?? '')
              : element.tickerCode
                ? 'ticker::' + element.tickerCode
                : 'row::' + byRegistrationNumber.size;
            const existing = byRegistrationNumber.get(key);
            if (!existing) {
              // First time we see this registration number; store the element as-is.
              byRegistrationNumber.set(key, element);
            } else {
              // Merge fields in both directions so ordering does not matter.
              if (!existing.tickerCode && element.tickerCode) {
                existing.tickerCode = element.tickerCode;
              }
              if (!existing.digitalCert && element.digitalCert) {
                existing.digitalCert = element.digitalCert;
              }
            }
          }
          return Array.from(byRegistrationNumber.values());
        }
        const portfolioData: Array<PortfolioListModel> = Array.isArray(data?.result?.data) ? data.result.data : [];
        mergedPortfolio = mergePortfolios(portfolioData);
        return {
          data: mergedPortfolio,
          summary: data.result.summary,
          total: data.result.totalCount,
          monthlyStatements: data.result.monthlyStatements ?? [],
        };
    })
  );
  }

  // Additive API alongside getPortfolio()/`/details` — same response shape plus
  // Wac/CostBasis/RealisedGainLoss/TotalGainLoss. The old endpoint/logic is untouched.
  getPortfolioGl(currentPage: number, take: number, isDigitalCert?: boolean, includeMonthlyStatements = false): Observable<any> {
    type GetPortfolioParams = {
      currentPage: string;
      take: string;
      isDigitalCert?: boolean;
      includeMonthlyStatements: boolean;
    };

    let params: GetPortfolioParams = {
      currentPage: currentPage.toString(),
      take: take.toString(),
      includeMonthlyStatements: includeMonthlyStatements,
    };

    if (isDigitalCert) {
      params.isDigitalCert = true;
    }

    return this.http.get(this.route + '/detailsGl', { params: params }).pipe(
      map((data: any) => {
        let mergedPortfolio: Array<PortfolioListModel> = [];
        let mergePortfolios = (arr: Array<PortfolioListModel>) => {
          const byRegistrationNumber = new Map<string, PortfolioListModel>();
          for (const element of arr) {
            // A missing registrationNumber must not become a shared "undefined"/"null" key
            // that merges unrelated companies together. Key on tickerCode when present (it's
            // unique per counter); otherwise fall back to a key unique to this row so it never
            // collides with another row lacking a registration number.
            const registrationNumber = element.company?.registrationNumber;
            const key = registrationNumber
              ? 'reg::' + String(registrationNumber) + '::' + (element.tickerCode ?? '')
              : element.tickerCode
                ? 'ticker::' + element.tickerCode
                : 'row::' + byRegistrationNumber.size;
            const existing = byRegistrationNumber.get(key);
            if (!existing) {
              byRegistrationNumber.set(key, element);
            } else {
              if (!existing.tickerCode && element.tickerCode) {
                existing.tickerCode = element.tickerCode;
              }
              if (!existing.digitalCert && element.digitalCert) {
                existing.digitalCert = element.digitalCert;
              }
            }
          }
          return Array.from(byRegistrationNumber.values());
        };
        const portfolioData: Array<PortfolioListModel> = Array.isArray(data?.result?.data) ? data.result.data : [];
        mergedPortfolio = mergePortfolios(portfolioData);
        return {
          data: mergedPortfolio,
          summary: data.result.summary,
          total: data.result.totalCount,
          monthlyStatements: data.result.monthlyStatements ?? [],
        };
      }),
    );
  }

  markPortfolioAsRead(): Observable<boolean> {
    return this.http
    .post<{ result: boolean }>(this.route + '/read',{})
    .pipe(map((response) => response.result));
  }

  getTaxExemptionCertificates(): Observable<any[]> {
    const url = environment.apiServer + 'Api/v1/Users/{userId}/taxExemptions/certificates';
    return this.http.get(url).pipe(
      map((response: any) => {
        const result = response?.result;
        if (Array.isArray(result)) {
          return result;
        }
        if (Array.isArray(result?.data)) {
          return result.data;
        }
        return [];
      }),
    );
  }

  getTaxExemptionCertificatePdfUrl(id: number | string): string {
    return environment.apiServer + 'api/v1/TaxExemptions/certificates/' + id + '/pdf';
  }

  getPortfolioHistory(filter: HistoryFilter): Observable<PagedResult<PortfolioHistoryList>> {
    const params: any = Object.assign({}, filter);
    if (params.transactionDateFrom)
      params.transactionDateFrom = moment(params.transactionDateFrom).startOf('day').utc().format();
    if (params.transactionDateTo)
      params.transactionDateTo = moment(params.transactionDateTo).startOf('day').utc().format();

    return this.http.get(this.route + '/history', { params: params }).pipe(
      map((data: any) => {
        return {
          data: data.result.data || [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  generateHistoryReportPdf(filter: HistoryFilter): Observable<any> {
    const params: any = Object.assign({}, filter);
    if (params.transactionDateFrom)
      params.transactionDateFrom = moment(params.transactionDateFrom).startOf('day').utc().format();
    if (params.transactionDateTo)
      params.transactionDateTo = moment(params.transactionDateTo).startOf('day').utc().format();

    return this.http.get(this.route + '/history/export', { params: params }).pipe(
      map((response: any) => {
        return {
          data: response.result,
        };
      }),
    );
  }

  getAnnouncements(currentPage: number, take: number): Observable<any> {
    let params = {
      currentPage: currentPage.toString(),
      take: take.toString(),
    };

    return this.http.get(this.route + '/updates', { params: params }).pipe(
      map((data: any) => {
        return {
          data: data.result.data || [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  getReports(): Observable<any> {
    return this.http.get(this.route + '/reports').pipe(
      map((data: any) => {
        return {
          data: data.result || [],
        };
      }),
    );
  }
}
